import { z } from 'zod';

export const PaperSchema = z.enum(['ruled', 'grid', 'dotted', 'blank', 'pdf']);
export type Paper = z.infer<typeof PaperSchema>;

export const NotebookSchema = z.object({
  id: z.string(),
  title: z.string(),
  color: z.string(),
  sort: z.number(),
  createdAt: z.number(),
  updatedAt: z.number(),
  deletedAt: z.number().nullable().optional(),
});
export type Notebook = z.infer<typeof NotebookSchema>;

export const PageSchema = z.object({
  id: z.string(),
  notebookId: z.string(),
  title: z.string(),
  pageDate: z.string(),
  paper: PaperSchema,
  defaultProject: z.string().nullable().optional(),
  sort: z.number(),
  transcript: z.string().nullable().optional(),
  createdAt: z.number(),
  updatedAt: z.number(),
  deletedAt: z.number().nullable().optional(),
});
export type Page = z.infer<typeof PageSchema>;

export const StrokeWireSchema = z.object({
  id: z.string(),
  pageId: z.string(),
  // Aceita 'pen' por compatibilidade: é como as pontas eram gravadas antes
  // de existirem os tipos de ponta.
  tool: z.enum(['pen', 'ballpoint', 'fountain', 'pencil', 'marker', 'calligraphy', 'highlighter']),
  color: z.string(),
  size: z.number(),
  opacity: z.number().min(0).max(1).default(1),
  points: z.string(),
  bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  row: z.number().int(),
  createdAt: z.number(),
  audioOffsetMs: z.number().nullable().optional(),
  deletedAt: z.number().nullable().optional(),
  processedRunId: z.string().nullable().optional(),
});

export const TextBlockSchema = z.object({
  id: z.string(),
  pageId: z.string(),
  x: z.number(),
  y: z.number(),
  width: z.number(),
  text: z.string(),
  row: z.number().int(),
  processedRunId: z.string().nullable().optional(),
  createdAt: z.number(),
  updatedAt: z.number(),
  deletedAt: z.number().nullable().optional(),
});
export type TextBlock = z.infer<typeof TextBlockSchema>;

export const SyncOpSchema = z.discriminatedUnion('entity', [
  z.object({ op: z.enum(['put', 'delete']), entity: z.literal('notebook'), id: z.string(), payload: NotebookSchema.partial().optional(), at: z.number() }),
  z.object({ op: z.enum(['put', 'delete']), entity: z.literal('page'), id: z.string(), payload: PageSchema.partial().optional(), at: z.number() }),
  z.object({ op: z.enum(['put', 'delete']), entity: z.literal('stroke'), id: z.string(), payload: StrokeWireSchema.optional(), at: z.number() }),
  z.object({ op: z.enum(['put', 'delete']), entity: z.literal('textBlock'), id: z.string(), payload: TextBlockSchema.partial().optional(), at: z.number() }),
]);
export type SyncOp = z.infer<typeof SyncOpSchema>;

export const PushRequestSchema = z.object({ ops: z.array(SyncOpSchema).max(2000) });
export const PullQuerySchema = z.object({ since: z.coerce.number().default(0) });

export interface PullResponse {
  cursor: number;
  notebooks: Notebook[];
  pages: Page[];
  strokes: z.infer<typeof StrokeWireSchema>[];
  textBlocks: TextBlock[];
}

/** Uma entrada da fila local. Só sai daqui quando o servidor confirma. */
export interface OutboxEntry {
  seq?: number;
  op: SyncOp;
}
